import { isDynamicMLSelectionEnabled } from 'shared/utils/workspaceUtils'
import { ContentItemViewNodesType } from '../types'

/**
 * Finds the BOD paragraph node (a pure BOD paragraph, not a mixed MFR/BOD node).
 */
export const findBodParagraph = (
    nodes: Partial<ContentItemViewNodesType>[] | null | undefined
): Partial<ContentItemViewNodesType> | undefined =>
    nodes?.find((n) => n?.isBOD === true && n?.isManufacturers !== true)

/**
 * Finds the reference node used to activate the manufacturer section. At family level a mixed
 * paragraph (isManufacturers && isBOD) exists and is preferred; at PT level there is no
 * separate manufacturer paragraph, so the first MFR pill (isManufacturers, not a BOD copy)
 * works as the reference for the visibility sync.
 */
export const findManufacturerParagraph = (
    nodes: Partial<ContentItemViewNodesType>[] | null | undefined
): Partial<ContentItemViewNodesType> | undefined =>
    nodes?.find((n) => n?.isManufacturers === true && n?.isBOD === true) ||
    nodes?.find(
        (n) =>
            n?.isManufacturers === true &&
            n?.isBOD !== true &&
            !n?.id?.includes('_bod_')
    )

/**
 * Whether a paragraph section is currently active (present and not hidden). Used to gate the
 * "first listing add" auto-hide of the opposite section: we only auto-hide when the section
 * being added was NOT already active.
 */
export const isSectionActive = (
    paragraph: Partial<ContentItemViewNodesType> | null | undefined
): boolean => !!paragraph && !(paragraph.isHidden ?? false)

/**
 * Node type classifications for BOD/Manufacturer logic
 */
export enum NodeClassification {
    BOD_PILL = 'bod-pill',
    BOD_PARAGRAPH = 'bod-paragraph',
    MANUFACTURER_PILL = 'manufacturer-pill',
    MANUFACTURER_PARAGRAPH = 'manufacturer-paragraph',
    OTHER = 'other',
}

/**
 * Classifies a node into one of the predefined categories
 */
export const classifyNode = (
    node: Partial<ContentItemViewNodesType>
): NodeClassification => {
    const isBODPill = node.id?.includes('_bod_') === true
    const isManufacturerNode = node.isManufacturers === true
    const isBODNode = node.isBOD === true

    // BOD pills are identified by _bod_ in their ID
    if (isBODPill) {
        return NodeClassification.BOD_PILL
    }

    // Mixed paragraph (both BOD and Manufacturer flags)
    // Note: Paragraphs with both flags are treated as Manufacturer paragraphs because
    // they represent the container that can display either BOD or Manufacturer content,
    // but the paragraph itself is controlled by the Manufacturer toggle in the UI.
    if (isManufacturerNode && isBODNode) {
        return NodeClassification.MANUFACTURER_PARAGRAPH
    }

    // BOD paragraph
    if (isBODNode && !isManufacturerNode) {
        return NodeClassification.BOD_PARAGRAPH
    }

    // Manufacturer elements - distinguish between paragraphs and pills by ID pattern
    if (isManufacturerNode && !isBODNode) {
        const looksLikePill =
            node.id?.includes('pill') ||
            (node.id?.includes('_') && node.id !== 'manufacturer-para')
        return looksLikePill
            ? NodeClassification.MANUFACTURER_PILL
            : NodeClassification.MANUFACTURER_PARAGRAPH
    }

    return NodeClassification.OTHER
}

/**
 * Checks if there are conflicts between BOD and Manufacturer paragraph states
 */
export const checkForConflicts = (
    nodes: Partial<ContentItemViewNodesType>[]
): { isValid: boolean; conflicts: string[] } => {
    const activeBODParagraphs = nodes.filter(
        (node) =>
            classifyNode(node) === NodeClassification.BOD_PARAGRAPH &&
            !node.isHidden
    )

    const activeManufacturerParagraphs = nodes.filter(
        (node) =>
            classifyNode(node) === NodeClassification.MANUFACTURER_PARAGRAPH &&
            !node.isHidden
    )

    if (
        activeBODParagraphs.length > 0 &&
        activeManufacturerParagraphs.length > 0
    ) {
        return {
            isValid: false,
            conflicts: [
                ...activeBODParagraphs.map((n) => n.id || 'unknown'),
                ...activeManufacturerParagraphs.map((n) => n.id || 'unknown'),
            ],
        }
    }

    return { isValid: true, conflicts: [] }
}

/**
 * Reference type for decision matrix
 */
type RefType = 'BOD' | 'MFR' | 'NONE'

/**
 * Node type for decision matrix
 */
type NodeType = 'BOD_PILL' | 'MFR_PILL' | 'MFR_PARA' | 'BOD_PARA' | 'OTHER'

/**
 * Decision actions for visibility logic
 */
type Decision = 'TOGGLE' | 'KEEP' | 'DEACTIVATE_ON_ACTIVATE' | 'NONE'

/**
 * Determines the node type based on node properties
 */
const determineNodeType = (
    isBODPill: boolean,
    isManufacturerNode: boolean,
    isBODNode: boolean
): NodeType => {
    if (isBODPill) return 'BOD_PILL'
    if (isManufacturerNode && !isBODNode) return 'MFR_PILL'
    if (isManufacturerNode && isBODNode) return 'MFR_PARA'
    if (isBODNode) return 'BOD_PARA'
    return 'OTHER'
}

/**
 * Decision matrix for dynamic mode (independent paragraphs)
 */
const dynamicDecisionMatrix: Record<RefType, Record<NodeType, Decision>> = {
    BOD: {
        BOD_PILL: 'TOGGLE', // BOD pills follow BOD paragraph state
        MFR_PILL: 'KEEP', // Independent
        MFR_PARA: 'KEEP', // Independent
        BOD_PARA: 'TOGGLE', // Match state
        OTHER: 'NONE',
    },
    MFR: {
        BOD_PILL: 'KEEP', // Independent
        MFR_PILL: 'TOGGLE', // Follow Manufacturer state
        MFR_PARA: 'TOGGLE', // Match state
        BOD_PARA: 'KEEP', // Independent
        OTHER: 'NONE',
    },
    NONE: {
        BOD_PILL: 'NONE',
        MFR_PILL: 'NONE',
        MFR_PARA: 'NONE',
        BOD_PARA: 'NONE',
        OTHER: 'NONE',
    },
}

/**
 * Decision matrix for legacy mode (mutual exclusivity)
 */
const legacyDecisionMatrix: Record<RefType, Record<NodeType, Decision>> = {
    BOD: {
        BOD_PILL: 'TOGGLE',
        MFR_PILL: 'DEACTIVATE_ON_ACTIVATE', // Deactivate when BOD activated
        MFR_PARA: 'DEACTIVATE_ON_ACTIVATE', // Deactivate when BOD activated
        BOD_PARA: 'TOGGLE',
        OTHER: 'KEEP',
    },
    MFR: {
        BOD_PILL: 'DEACTIVATE_ON_ACTIVATE', // Deactivate when Manufacturer activated
        MFR_PILL: 'TOGGLE',
        MFR_PARA: 'TOGGLE',
        BOD_PARA: 'DEACTIVATE_ON_ACTIVATE', // Deactivate when Manufacturer activated
        OTHER: 'KEEP',
    },
    NONE: {
        BOD_PILL: 'KEEP',
        MFR_PILL: 'KEEP',
        MFR_PARA: 'KEEP',
        BOD_PARA: 'KEEP',
        OTHER: 'KEEP',
    },
}

/**
 * Applies the decision to determine final visibility state
 */
const applyDecision = (
    decision: Decision,
    shouldBeHidden: boolean,
    currentIsHidden: boolean
): boolean => {
    switch (decision) {
        case 'TOGGLE':
            return shouldBeHidden
        case 'KEEP':
            return currentIsHidden
        case 'DEACTIVATE_ON_ACTIVATE':
            return !shouldBeHidden ? true : currentIsHidden
        case 'NONE':
        default:
            return currentIsHidden
    }
}

/**
 * Calculates the visibility of a node based on BOD/Manufacturer reference node logic
 */
export const calculateNodeVisibility = (
    node: Partial<ContentItemViewNodesType>,
    referenceNode: Partial<ContentItemViewNodesType>,
    shouldBeHidden: boolean
): boolean => {
    const isReferenceBOD = referenceNode.isBOD === true
    const isReferenceManufacturer = referenceNode.isManufacturers === true

    // Early return if reference node is neither BOD nor Manufacturer, or if nodes have different parents
    // Should only updates MFR/BOD under the same Product Type
    // Exception: always update the reference node itself regardless of dtvParentId
    // (BOD paragraphs can have dtvParentId === null, which would otherwise skip them)
    const isReferenceNode = !!node.id && node.id === referenceNode.id
    const nodeHasSameParent = isReferenceNode || (node.dtvParentId !== null && node.dtvParentId === referenceNode.dtvParentId)
    if ((!isReferenceBOD && !isReferenceManufacturer) || !nodeHasSameParent) {
        return node.isHidden ?? false
    }

    const currentIsHidden = node.isHidden ?? false
    const isBODPill = node.id?.includes('_bod_') === true
    const isManufacturerNode = node.isManufacturers === true
    const isBODNode = node.isBOD === true

    // Determine types
    const refType: RefType = isReferenceBOD ? 'BOD' : isReferenceManufacturer ? 'MFR' : 'NONE'
    const nodeType = determineNodeType(isBODPill, isManufacturerNode, isBODNode)

    // Select decision matrix
    const matrix = isDynamicMLSelectionEnabled() ? dynamicDecisionMatrix : legacyDecisionMatrix
    const decision = matrix[refType][nodeType]

    return applyDecision(decision, shouldBeHidden, currentIsHidden)
}

/**
 * Checks if a BOD pill should be hidden based on its associated BOD paragraph
 * Returns null if not a BOD pill, boolean if it is a BOD pill
 */
export const shouldHideBODPill = (
    contentItemView: Partial<ContentItemViewNodesType>,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    dtvItemId?: string
): boolean | null => {
    if (classifyNode(contentItemView) !== NodeClassification.BOD_PILL) {
        return null
    }

    const bodParagraph = contentItemViewNodes.find(
        (civ) => civ.dtvItemId === contentItemView.refBodItemId
    )
    const isBodParagraphDisabled = bodParagraph?.isHidden ?? true

    // BOD pills should be hidden when BOD paragraph is disabled OR when item is explicitly hidden
    return !!dtvItemId || isBodParagraphDisabled
}

/**
 * Handles the reference node case: syncs isHidden and isDeactivated atomically.
 * In OM workspace, isHidden may be pre-set by the server while isDeactivated is still false,
 * so we skip only when both already match the target — not just one of them.
 * Returns the updated node, or null if this node is not the reference node.
 */
const syncReferenceNode = (
    node: Partial<ContentItemViewNodesType>,
    referenceNode: Partial<ContentItemViewNodesType>,
    currentIsHidden: boolean,
    shouldBeHidden: boolean
): Partial<ContentItemViewNodesType> | null => {
    if (node.id === undefined || node.id !== referenceNode.id) return null
    const currentDeactivated = node.isDeactivated ?? false
    if (currentIsHidden === shouldBeHidden && currentDeactivated === shouldBeHidden) return node
    return { ...node, isHidden: shouldBeHidden, isDeactivated: shouldBeHidden }
}

/**
 * Synchronizes visibility between BOD and Manufacturer nodes with validation
 */
export const syncVisibilityBetweenBODAndManufacturers = (
    referenceNode: Partial<ContentItemViewNodesType> | null | undefined,
    allNodes: Partial<ContentItemViewNodesType>[] | null | undefined,
    shouldBeHidden: boolean
): Partial<ContentItemViewNodesType>[] => {
    if (!referenceNode || !allNodes) {
        return allNodes || []
    }

    const isReferenceBOD = referenceNode.isBOD === true
    const isReferenceManufacturer = referenceNode.isManufacturers === true

    // Early return if reference node is neither BOD nor Manufacturer
    if (!isReferenceBOD && !isReferenceManufacturer) {
        return allNodes
    }

    if (!isDynamicMLSelectionEnabled()) {
        // Old behavior: Auto-activation when deactivating Manufacturer
        const bodParagraph = allNodes.find(
            (n) => n?.isBOD === true && n?.isManufacturers !== true
        )
        const manufacturerParagraph = allNodes.find(
            (n) => n?.isManufacturers === true && n?.isBOD !== true
        )
        const isBODActive = bodParagraph ? !(bodParagraph.isHidden ?? false) : false
        const isManufacturerActive = manufacturerParagraph
            ? !(manufacturerParagraph.isHidden ?? false)
            : false

        // When deactivating Manufacturer paragraph, activate BOD paragraph
        const shouldActivateBODOnManufacturerDeactivation =
            isReferenceManufacturer &&
            shouldBeHidden &&
            isManufacturerActive &&
            !isBODActive

        const updatedNodes = allNodes.map((node) => {
            const currentIsHidden = node.isHidden ?? false
            const nodeHasSameParent = node.dtvParentId !== null && node.dtvParentId === referenceNode.dtvParentId

            const refResult = syncReferenceNode(node, referenceNode, currentIsHidden, shouldBeHidden)
            if (refResult !== null) return refResult

            let newIsHidden

            // Also should only updates MFR/BOD under the same Product Type
            if (shouldActivateBODOnManufacturerDeactivation && nodeHasSameParent) {
                // Activate BOD elements when deactivating sole active Manufacturer
                const isBODElement =
                    (node?.isBOD === true && node?.isManufacturers !== true) ||
                    (node?.isManufacturers === true && node?.id?.includes('_bod_'))
                const isManufacturerElement =
                    node?.isManufacturers === true && !node?.id?.includes('_bod_')

                if (isBODElement) {
                    newIsHidden = false // Activate BOD elements
                } else if (isManufacturerElement) {
                    newIsHidden = true // Deactivate Manufacturer elements
                } else {
                    newIsHidden = currentIsHidden // Leave others unchanged
                }
            } else {
                newIsHidden = calculateNodeVisibility(
                    node,
                    referenceNode,
                    shouldBeHidden
                )
            }

            // Sync isDeactivated with isHidden for OM workspace optimistic updates
            return newIsHidden !== currentIsHidden
                ? { ...node, isHidden: newIsHidden, isDeactivated: newIsHidden }
                : node
        })

        // Check for conflicts after update
        const validation = checkForConflicts(updatedNodes)
        if (!validation.isValid) {
            console.warn(
                'Conflict detected: BOD and Manufacturer paragraphs cannot both be active. Conflicting nodes:',
                validation.conflicts
            )
        }

        return updatedNodes
    }

    // New behavior: Independent paragraphs
    const updatedNodes = allNodes.map((node) => {
        const currentIsHidden = node.isHidden ?? false

        const refResult = syncReferenceNode(node, referenceNode, currentIsHidden, shouldBeHidden)
        if (refResult !== null) return refResult

        const newIsHidden = calculateNodeVisibility(
            node,
            referenceNode,
            shouldBeHidden
        )

        // Sync isDeactivated with isHidden for OM workspace optimistic updates
        return newIsHidden !== currentIsHidden
            ? { ...node, isHidden: newIsHidden, isDeactivated: newIsHidden }
            : node
    })

    return updatedNodes
}
